import { defineStore } from 'pinia';
import { getCurrentInstance } from 'vue';
import { useToast } from 'vue-toast-notification';
import { useLoadingStore } from '@/pinia/loading.module';
import { useAuthStore } from '@/pinia/auth.module';
import { getLocaleMessage } from '@/services/MessagesService';
import { getNestedValue } from '@/helpers/sort';
import * as service from '@/services';

const $toast = useToast();
const endPointCreditFacilities = "/api/creditFacilities";

export const useCreditFacilities = defineStore('creditFacilities', {
    /**
     * Estado central del store.
     */
    state: () => ({
        /**Datos de tabla */
        sortKey: '',
        sortOrder: 1, // 1 for ascending, -1 for descending
        itemsPerPage: 10, //Por default 10 por pagina
        currentPage: 1,
        searchString: '',
        creditFacilities: []
    }),
    /**
     * Getters para filtrar, ordenar y paginar disposición.
     */
    getters: {
        /**
         * Devuelve las carátulas filtradas por el texto de búsqueda.
         */
        filteredS: (state) => {
            if (!state.searchString) return state.creditFacilities;

            //#region control de fecha string
            const instance = getCurrentInstance();
            const $filters = instance?.appContext.config.globalProperties.$filters;
            //#endregion control de fecha string

            const searchLower = state.searchString.toLowerCase();
            return state.creditFacilities.filter(creditFacility => {
                const firstNameProspect = creditFacility?.prospectus?.generalInformation?.firstName ? creditFacility.prospectus?.generalInformation?.firstName?.toLowerCase() : '';
                const lastNameProspect = creditFacility?.prospectus?.generalInformation?.lastName ? creditFacility.prospectus?.generalInformation?.lastName?.toLowerCase() : '';
                const mothersMaidenNameProspect = creditFacility?.prospectus?.generalInformation?.mothersMaidenName ? creditFacility.prospectus?.generalInformation?.mothersMaidenName?.toLowerCase() : '';
                const nameProspect = creditFacility?.prospectus?.generalInformation?.businessName === null ?
                    `${firstNameProspect} ${lastNameProspect} ${mothersMaidenNameProspect}`
                    :
                    creditFacility?.prospectus?.generalInformation?.businessName ? creditFacility.prospectus?.generalInformation?.businessName?.toLowerCase() : '';

                return (
                    creditFacility?.referenceNumber?.toLowerCase().includes(searchLower) ||
                    creditFacility?.prospectus?.referenceNumber?.toLowerCase().includes(searchLower) ||
                    creditFacility?.loanApplication?.referenceNumber?.toLowerCase().includes(searchLower) ||
                    creditFacility?.prospectus.personType?.name?.toLowerCase().includes(searchLower) ||
                    $filters?.formatDate(creditFacility.createdAt)?.toLowerCase().includes(searchLower) ||
                    $filters?.formatDate(creditFacility.receiptPaymentFiles.updatedAt)?.toLowerCase().includes(searchLower) ||
                    nameProspect.includes(searchLower) ||
                    creditFacility?.status?.name?.toLowerCase().includes(searchLower)
                );
            });
        },
        /**
         * Ordena los registros de la tabla de Dispoción según la clave de orden actual.
         */
        sortedS: (state) => {
            // Logica de ordenamiento
            let sortedList = [...state.filteredS];
            if (state.sortKey) {
                sortedList.sort((a, b) => {

                    // Caso especial para el nombre
                    if (state.sortKey === 'name') {
                        const getNameForSort = (item) => {
                            if (!item) return '';

                            if (item.prospectus.generalInformation.businessName === null) {
                                return `${item.prospectus?.generalInformation?.firstName || ''} ${item.prospectus?.generalInformation?.lastName || ''} ${item.prospectus?.generalInformation?.mothersMaidenName || ''}`.toLowerCase();
                            } else {
                                return (item.prospectus?.generalInformation?.businessName || '').toLowerCase();
                            }
                        };

                        const aName = getNameForSort(a);
                        const bName = getNameForSort(b);
                        if (aName < bName) return -1 * state.sortOrder;
                        if (aName > bName) return 1 * state.sortOrder;
                        return 0;
                    }

                    // Ordenamiento normal para otras columnas
                    let aValue = getNestedValue(a, state.sortKey);
                    let bValue = getNestedValue(b, state.sortKey);

                    if (aValue < bValue) return -1 * state.sortOrder;
                    if (aValue > bValue) return 1 * state.sortOrder;
                    return 0;
                });
            }
            return sortedList;
        },
        /**
         * Aplica paginación a la lista ordenada.
         */
        paginatedS: (state) => {
            if (!state.sortedS || state.sortedS.length === 0) return [];
            let start = (state.currentPage * state.itemsPerPage) - state.itemsPerPage;
            return state.sortedS.slice(start, start + state.itemsPerPage);
        },
        getCreditFacilities: state => state.creditFacilities
    },
    /**
     * Acciones asincrónicas y mutaciones del store.
     */
    actions: {
        /**
         * Obtiene todos los registros de disposición por compañia
         * Como reusltado llena el valor del creditFacilities para mostrar los registro de la tabla
         *  http://localhost:5075/api/CreditFacilities/company/1
        */
        async fetchCreditFacilities() {
            try {
                useLoadingStore().loading = true;
                const { data } = await service.creditFacilitiesApi.get(`${endPointCreditFacilities}/company/${useAuthStore().user.companyId}`);
                if (data && data.status) {
                    this.creditFacilities = data.data;
                }
            } catch (error) {
                this.handleError(error);
                throw error;
            } finally {
                useLoadingStore().loading = false;
            }
        },
        /**
         * Funcionalidad que permite la creación de un nuevo registro de disposición
         * Como resultado trae el nuevo registro agregado con sus relaciones correspondientes
         * @param {*} formData 
         * @returns
         *  http://localhost:5075/api/CreditFacilities
         */
        async createCreditFacility(formData) {
            try {
                useLoadingStore().loading = true;

                // Configurar headers para multipart/form-data
                const config = {
                    headers: {
                        'Content-Type': 'multipart/form-data'
                    }
                };

                const { data } = await service.creditFacilitiesApi.post(
                    `${endPointCreditFacilities}`,
                    formData,
                    config
                );

                if (data && data.status) {
                    $toast.success(getLocaleMessage('creditFacilitiesRequest', 'creatingSuccess'));
                    return data;
                } else {
                    $toast.error(data?.error || getLocaleMessage('creditFacilitiesRequest', 'creatingError'));
                    return data;
                }
            } catch (error) {
                this.handleError(error);
                throw error;
            } finally {
                useLoadingStore().loading = false;
            }
        },
        /**
         * Funcionalidad que permite la actualización de un registro de disposición
         * Como resultado trae el nuevo registro agregado con sus relaciones correspondientes
         * @param {*} formData 
         * @returns
         * Id del creditfacility
         * http://localhost:5075/api/CreditFacilities/{id} 
         */
        async updateCreditFacility(formData) {
            try {
                useLoadingStore().loading = true;

                const id = formData.get('Id');

                // Configurar headers para multipart/form-data
                const config = {
                    headers: {
                        'Content-Type': 'multipart/form-data'
                    }
                };

                const { data } = await service.creditFacilitiesApi.put(
                    `${endPointCreditFacilities}/${id}`,
                    formData,
                    config
                );

                console.log("📥 Respuesta del servidor:", data);

                if (data && data.status) {
                    $toast.success(getLocaleMessage('creditFacilitiesRequest', 'editingSuccess'));
                    // 🔄 Actualizar la lista localmente para ver cambios inmediatos
                    await this.fetchCreditFacilities();
                    return data;
                } else {
                    $toast.error(data?.error || getLocaleMessage('creditFacilitiesRequest', 'editingError'));
                    await this.fetchCreditFacilities();
                    return data;
                }
            } catch (error) {
                this.handleError(error);
                throw error;
            } finally {
                useLoadingStore().loading = false;
            }
        },
        /**
         * Descarga un archivo por su nombre GUID
         * @param {string} guidName - GUID del archivo
         * @param {string} fileName - Nombre del archivo para la descarga
         * http://localhost:5075/api/CreditFacilities/GetFileByNameGuid?fileName=9f4b7cdf-5e43-4196-a606-e6c626acc663.pdf
         */
        async downloadFile(guidName, fileName) {
            try {
                useLoadingStore().loading = true;

                // Configurar la respuesta como blob para manejar archivos
                const config = {
                    responseType: 'blob'
                };

                // Usar el endpoint proporcionado
                const { data } = await service.creditFacilitiesApi.get(
                    `${endPointCreditFacilities}/GetFileByNameGuid?fileName=${guidName}`,
                    config
                );

                // Verificar que sea un Blob
                if (!(data instanceof Blob)) {
                    // Si no es un Blob, convertirlo manualmente
                    const blob = new Blob([data], { type: 'application/pdf' });
                    this.downloadBlob(blob, fileName || guidName || 'comprobante.pdf');
                } else {
                    // Si ya es un Blob, usarlo directamente
                    this.downloadBlob(data, fileName || guidName || 'comprobante.pdf');
                }

                return true;
            } catch (error) {
                this.handleError(error);
                console.error('Error downloading file:', error);
                $toast.error(data?.error || getLocaleMessage('creditFacilitiesRequest', 'errorDownloadingFile'));
                throw error;
            } finally {
                useLoadingStore().loading = false;
            }
        },
        /**
         * Método auxiliar para descargar el blob
         */
        downloadBlob(blob, fileName) {
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', fileName);
            document.body.appendChild(link);
            link.click();

            // Limpiar
            link.remove();
            window.URL.revokeObjectURL(url);
            $toast.success(getLocaleMessage('creditFacilitiesRequest', 'fileDownloadedSuccessfully'));
        },
        /**
         * Maneja errores en la comunicación con la API.
         */
        handleError(error) {
            this.errors = error;
            if (error.response) {
                console.log("Inspec handle ERROR IF: ", error)
                let msg = error?.response?.data?.error || getLocaleMessage('common', 'responseError');
                $toast.error(msg);
            } else if (error.request) {
                console.log("Inspec handle ERROR ELSE IF: ", error)
                let msg = error?.response?.data?.error || getLocaleMessage('common', 'networkError');
                $toast.error(msg);
            } else {
                console.log("Inspec handle ERROR ELSE: ", error)
                let msg = error?.response?.data?.error || getLocaleMessage('common', 'generalError');
                $toast.error(msg);
            }
        },
        /**
        * Cambia el orden de las columnas en la tabla.
        */
        sortBy(key) {
            if (this.sortKey === key) {
                this.sortOrder *= -1;
            } else {
                this.sortOrder = 1;
            }
            this.sortKey = key;
            this.currentPage = 1; // Resetear a la primera página al ordenar
        },
        /**
         * Establece la página actual del paginador.
         */
        setCurrentPage(page) {
            this.currentPage = page;
        },
        setSearchString(value) {
            this.searchString = value;
            this.currentPage = 1; // Resetear paginación al buscar
        },
        /**
         * http://localhost:5075/api/CreditFacilities/prospect/1034
         */
        async fetchCreditFacilitiesByProspect(prospectId) {
            try {
                useLoadingStore().loading = true;
                const { data } = await service.creditFacilitiesApi.get(`${endPointCreditFacilities}/prospect/${prospectId}`);
                if (data && data.status) {
                    this.creditFacilities = data.data;
                }
            } catch (error) {
                this.handleError(error);
                throw error;
            } finally {
                useLoadingStore().loading = false;
            }
        },
    }
});